import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, openProtectedFile } from "@/lib/api";
import { ageText, BREED_EXAMPLE, SPECIES, SPECIES_LABEL } from "@/lib/pets";
import PetAvatar from "@/components/PetAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Check, ChevronDown, FileText, HeartHandshake, Loader2, PawPrint, Pencil, Plus, Scissors, Stethoscope, Syringe, Trash2 } from "lucide-react";
import { RehomeDialog, RehomeStatus } from "@/components/Rehoming";
import PetCareForm, { careSummary } from "@/components/PetCareForm";

// My Pets: only the name and kind are needed; everything else is optional.
// We use it to pick products for each pet on the home page.
const EMPTY = { name: "", species: "dog", breed: "", gender: "", ageBy: "birthday", birthday: "", years: "", months: "", weight_kg: "", notes: "", photo_url: "" };
const todayIso = () => new Date().toISOString().slice(0, 10);

/** Age in years + months → an approximate birthday (first of that month). */
function birthFromAge(years, months) {
  const y = Number(years) || 0;
  const m = Number(months) || 0;
  if (!y && !m) return null;
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - (y * 12 + m));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function toForm(p) {
  return {
    name: p.name,
    species: p.species,
    breed: p.breed || "",
    gender: p.gender || "",
    // The real birthday if they gave it; otherwise the age they typed.
    ageBy: p.birth_exact || p.age_months == null ? "birthday" : "age",
    birthday: p.birth_exact ? p.birth_date || "" : "",
    years: p.age_months != null ? String(Math.floor(p.age_months / 12)) : "",
    months: p.age_months != null ? String(p.age_months % 12) : "",
    weight_kg: p.weight_kg ? String(p.weight_kg) : "",
    notes: p.notes || "",
    photo_url: p.photo_url || "",
    birth: p.birth_date,
    age: p.age_months,
  };
}

function Chip({ on, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      data-testid={testId}
      className={`px-4 h-9 rounded-full border text-sm font-medium transition-colors ${on ? "bg-stoneWarm text-white border-stoneWarm" : "bg-white border-stone-300 text-stone-700 hover:border-marigold"}`}
    >
      {children}
    </button>
  );
}

function PetForm({ pet, onDone, onClose }) {
  const [f, setF] = useState(() => (pet ? toForm(pet) : EMPTY));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  async function pickPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo is over 8 MB"); return; }
    const form = new FormData();
    form.append("file", file);
    form.append("purpose", "pet_photo");
    setUploading(true);
    try {
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url")(data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    if (!f.name.trim()) { setError("Enter your pet's name."); return; }
    const w = f.weight_kg.trim() ? Number(f.weight_kg) : null;
    if (w != null && !(w > 0 && w <= 150)) { setError("Weight should be in kg, e.g. 12.5"); return; }
    // A real birthday, or one worked out from the age (kept if the age wasn't changed).
    const ageNow = (Number(f.years) || 0) * 12 + (Number(f.months) || 0);
    const exact = f.ageBy === "birthday" && !!f.birthday;
    if (exact && f.birthday > todayIso()) { setError("The birthday can't be in the future."); return; }
    const birth = f.ageBy === "birthday" ? f.birthday || null : f.birth && ageNow === f.age ? f.birth : birthFromAge(f.years, f.months);
    const body = {
      name: f.name.trim(),
      species: f.species,
      breed: f.breed.trim() || null,
      gender: f.gender || null,
      birth_date: birth,
      birth_exact: exact,
      weight_kg: w,
      notes: f.notes.trim() || null,
      photo_url: f.photo_url || null,
    };
    setBusy(true);
    setError("");
    try {
      if (pet) await api.patch(`/customer/pets/${pet.id}`, body);
      else await api.post("/customer/pets", body);
      toast.success(pet ? "Saved" : `${body.name} added`);
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-5" data-testid="pet-form">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} className="relative" aria-label="Add a photo" data-testid="pet-photo-btn">
          <PetAvatar pet={f} size={84} />
          <span className="absolute right-0 bottom-0 h-8 w-8 rounded-full bg-stoneWarm text-white flex items-center justify-center">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden" data-testid="pet-photo-input" />
        <div className="text-sm text-stone-600">
          {uploading ? "Uploading…" : f.photo_url ? (
            <>Click the photo to change it · <button type="button" className="text-terracotta hover:underline" onClick={() => set("photo_url")("")}>Remove</button></>
          ) : "Add a photo (optional)"}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pet-name">Name *</Label>
        <Input id="pet-name" data-testid="pet-name" value={f.name} maxLength={40} placeholder="Bruno" onChange={(e) => set("name")(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Your pet is a *</Label>
        <div className="flex flex-wrap gap-2">
          {SPECIES.map((s) => <Chip key={s.value} on={f.species === s.value} onClick={() => set("species")(s.value)} testId={`pet-species-${s.value}`}>{s.label}</Chip>)}
        </div>
      </div>

      <div className="border-t border-stone-200 pt-4 space-y-4">
        <div className="text-sm font-semibold text-stone-700">More about them (optional)</div>
        <div className="space-y-1.5">
          <Label htmlFor="pet-breed">Breed</Label>
          <BreedPicker species={f.species} value={f.breed} onChange={set("breed")} placeholder={BREED_EXAMPLE[f.species]} />
          <p className="text-xs text-stone-500">Pick from the list, or type your own (mixed breeds too).</p>
        </div>
        <div className="space-y-1.5">
          <Label>Gender</Label>
          <div className="flex gap-2">
            {[["male", "Male"], ["female", "Female"], ["unknown", "Not sure"]].map(([v, l]) => (
              <Chip key={v} on={f.gender === v} onClick={() => set("gender")(f.gender === v ? "" : v)}>{l}</Chip>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Age</Label>
          <div className="flex flex-wrap gap-2">
            <Chip on={f.ageBy === "birthday"} onClick={() => set("ageBy")("birthday")} testId="pet-age-birthday">I know the birthday</Chip>
            <Chip on={f.ageBy === "age"} onClick={() => set("ageBy")("age")} testId="pet-age-rough">Roughly how old</Chip>
          </div>
        </div>
        {f.ageBy === "birthday" ? (
          <div className="space-y-1.5">
            <Label htmlFor="pet-birthday">Birthday</Label>
            <Input id="pet-birthday" type="date" data-testid="pet-birthday" value={f.birthday} min="1990-01-01" max={todayIso()}
              onChange={(e) => set("birthday")(e.target.value)} className="w-48" />
            <p className="text-xs text-stone-500">{f.birthday ? `${ageText(Math.max(0, Math.floor((Date.now() - new Date(`${f.birthday}T00:00:00`).getTime()) / (30.44 * 864e5))))} old · ` : ""}Shops and groomers you choose can wish them on the day (Profile › Privacy).</p>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="pet-years">Age: years</Label>
            <Input id="pet-years" inputMode="numeric" placeholder="Years" value={f.years} onChange={(e) => set("years")(e.target.value.replace(/\D/g, "").slice(0, 2))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pet-months">and months</Label>
            <Input id="pet-months" inputMode="numeric" placeholder="Months" value={f.months}
                   onChange={(e) => { const n = e.target.value.replace(/\D/g, ""); set("months")(n === "" ? "" : String(Math.min(11, Number(n)))); }} />
          </div>
        </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="pet-weight">Weight (kg)</Label>
          <Input id="pet-weight" inputMode="decimal" placeholder="e.g. 24" value={f.weight_kg} onChange={(e) => set("weight_kg")(e.target.value.replace(/[^\d.]/g, ""))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pet-notes">Notes</Label>
          <Textarea id="pet-notes" rows={3} maxLength={500} placeholder="Allergies, favourite food, anything we should know" value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
        </div>
      </div>

      {error && <p className="text-sm text-terracotta" data-testid="pet-error">{error}</p>}
      <DialogFooter className="gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={busy || uploading} data-testid="pet-save" className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
          {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {pet ? "Save" : "Add pet"}
        </Button>
      </DialogFooter>
    </form>
  );
}

const dayText = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** The next vaccine due: the latest dose of each vaccine counts. */
function nextDose(rows) {
  const latest = {};
  for (const r of rows) {
    const k = r.vaccine.toLowerCase();
    if (!latest[k] || r.given_on > latest[k].given_on) latest[k] = r;
  }
  return Object.values(latest).filter((r) => r.next_due).sort((a, b) => a.next_due.localeCompare(b.next_due))[0] || null;
}

function VaccineBadge({ rows }) {
  if (!rows.length) return null;
  const next = nextDose(rows);
  const today = new Date().toISOString().slice(0, 10);
  const days = next ? Math.round((new Date(`${next.next_due}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000) : null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid="pet-vaccinated">
      <span className="inline-flex items-center gap-1 rounded-full bg-teal/10 text-teal font-semibold px-2 py-0.5"><Syringe className="h-3 w-3" /> Vaccinated</span>
      {next && (
        <span className={days < 0 ? "text-terracotta font-medium" : days <= 7 ? "text-marigold font-medium" : "text-stone-600"} data-testid="pet-next-dose">
          Next: {next.vaccine} {days < 0 ? `was due ${dayText(next.next_due)} (overdue)` : days === 0 ? "due today" : `on ${dayText(next.next_due)}${days <= 7 ? ` (in ${days} day${days === 1 ? "" : "s"})` : ""}`}
        </span>
      )}
    </div>
  );
}

/** Vaccinations NGOs recorded for this pet (at drives, or before adoption).
 *  We send a reminder a week before a dose is due, and on the day. */
function HealthRecords({ rows }) {
  if (!rows.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mt-3 border-t border-stone-100 pt-3" data-testid="pet-records">
      <div className="text-xs font-semibold uppercase tracking-wide text-teal flex items-center gap-1"><Syringe className="h-3.5 w-3.5" /> Health records</div>
      <ul className="mt-2 space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="text-sm flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{r.vaccine}</span>
            <span className="text-stone-600">{dayText(r.given_on)}</span>
            {r.next_due && <span className={r.next_due < today ? "text-terracotta font-medium" : "text-stone-600"}>· next due {dayText(r.next_due)}{r.next_due < today ? " (overdue)" : ""}</span>}
            <span className="basis-full text-xs text-stone-500">{r.recorded_by_label}{r.drive_title ? ` at ${r.drive_title}` : ""} · {dayText(r.given_on)}. Not a vet certificate.</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What the vet found and gave at one visit (the owner's own record). */
function VisitDetails({ v }) {
  const lines = [
    v.vaccine && `Vaccine: ${v.vaccine}`,
    v.findings && `Found: ${v.findings}`,
    v.advice && `Advice: ${v.advice}`,
    v.medicines?.length && `Medicines: ${v.medicines.map((m) => `${m.name}${m.instruction ? ` (${m.instruction})` : ""}`).join("; ")}`,
    v.given_in_clinic?.length && `Given at the clinic: ${v.given_in_clinic.map((g) => `${g.label} ${g.qty} ${g.unit || ""}`.trim()).join(", ")}`,
    v.procedures?.length && `Procedures: ${v.procedures.map((p) => p.name).join(", ")}`,
    v.follow_up && `Follow-up: ${dayText(v.follow_up.date)}${v.follow_up.note ? ` (${v.follow_up.note})` : ""}`,
    v.noticed && `Noticed: ${v.noticed}`,
  ].filter(Boolean);
  if (!lines.length && !v.has_prescription) return null;
  return (
    <span className="block mt-1 rounded-lg bg-stone-50 px-2 py-1.5 text-xs text-stone-700 space-y-0.5" data-testid={`pet-visit-${v.booking_id}`}>
      {lines.map((l) => <span key={l} className="block">{l}</span>)}
      {v.has_prescription && <button type="button" className="text-teal font-semibold inline-flex items-center gap-1"
        onClick={() => openProtectedFile(`/care/me/bookings/${v.booking_id}/prescription.pdf`).catch(() => toast.error("Couldn't open the prescription"))}>
        <FileText className="h-3 w-3" />Prescription</button>}
    </span>
  );
}

const CARE_URL = (process.env.REACT_APP_CARE_URL || "https://care.pawbazaar.store").replace(/\/$/, "");

/** CUS-8: one pet's history, built to stay readable over years: where each
 *  vaccine stands, then everything that happened, year by year (newest
 *  first, older years folded), filterable by vaccine. */
function PetHistory({ pet, records, onClose }) {
  const [upcoming, setUpcoming] = useState([]);
  const [hist, setHist] = useState(null);   // finished visits with what the vet found and gave (care/pet_history.py)
  const [only, setOnly] = useState("");
  const [earlier, setEarlier] = useState(null);   // health history from an adopted pet's earlier family
  const loadHist = useCallback((page) => api.get(`/care/me/pets/${pet.id}/history`, { params: { page } })
    .then(({ data }) => setHist((h) => ({ ...data, visits: page > 1 ? [...(h?.visits || []), ...data.visits] : data.visits })),
      () => setHist((h) => h || { visits: [], total: 0, page: 1, pages: 1 })), [pet?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pet) return;
    setOnly(""); setHist(null); setEarlier(null);
    loadHist(1);
    if (pet.adopted_from) api.get(`/community/me/pets/${pet.id}/earlier-history`).then(({ data }) => setEarlier(data?.history || null), () => {});
    api.get("/care/me/bookings").then(({ data }) => setUpcoming((data || []).filter((b) => b.pet?.id === pet.id && ["in_progress", "confirmed"].includes(b.status))),
      () => setUpcoming([]));
  }, [pet, loadHist]);
  const visits = hist && [...upcoming, ...hist.visits];
  if (!pet) return null;
  const rows = records.filter((r) => r.pet_id === pet.id);
  const today = new Date().toISOString().slice(0, 10);
  const latest = {};
  for (const r of rows) { const k = r.vaccine.toLowerCase(); if (!latest[k] || r.given_on > latest[k].given_on) latest[k] = r; }
  const status = (r) => !r.next_due ? ["No next dose", "bg-stone-100 text-stone-600"]
    : r.next_due < today ? ["Overdue", "bg-terracotta/15 text-terracotta"]
    : (new Date(`${r.next_due}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000 <= 30 ? ["Due soon", "bg-marigold/20 text-stoneWarm"]
    : ["Up to date", "bg-teal/10 text-teal"];
  const events = [
    ...rows.filter((r) => !only || r.vaccine.toLowerCase() === only).map((r) => ({ id: r.id, date: r.given_on, kind: "vaccine", title: r.vaccine,
      sub: `${r.recorded_by_label}${r.drive_title ? ` at ${r.drive_title}` : ""}${r.next_due ? ` · next due ${dayText(r.next_due)}` : ""}` })),
    ...(only ? [] : upcoming.map((b) => ({ id: b.id, date: b.date, kind: b.service?.category?.startsWith("vet_") ? "vet" : "care", title: b.service?.name,
      sub: `${b.salon_name || b.salon?.name || "Care provider"} · ${b.status_label}` }))),
    ...(only ? [] : (hist?.visits || []).map((v) => ({ id: v.booking_id, date: v.date, kind: v.vet ? "vet" : "care", title: v.name || v.category_label,
      sub: `${v.place || "Care provider"}${v.by ? ` · ${v.by}` : ""}`, visit: v }))),
  ].sort((a, b) => b.date.localeCompare(a.date));
  const years = [...new Set(events.map((e) => e.date.slice(0, 4)))];
  const thisYear = today.slice(0, 4);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pet-history">
        <DialogHeader><DialogTitle>{pet.name}: health and care history</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-stone-50 p-3"><div className="text-xl font-bold">{rows.length}</div><div className="text-[11px] uppercase tracking-widest text-stone-500">Vaccine doses</div></div>
          <div className="rounded-xl bg-stone-50 p-3"><div className="text-xl font-bold">{Object.keys(latest).length}</div><div className="text-[11px] uppercase tracking-widest text-stone-500">Vaccines</div></div>
          <div className="rounded-xl bg-stone-50 p-3"><div className="text-xl font-bold">{visits ? upcoming.length + hist.total : "…"}</div><div className="text-[11px] uppercase tracking-widest text-stone-500">Care visits</div></div>
        </div>
        {Object.keys(latest).length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-teal mb-2">Where each vaccine stands</div>
            <ul className="divide-y divide-stone-100 rounded-xl border border-stone-200" data-testid="pet-history-vaccines">
              {Object.values(latest).sort((a, b) => (a.next_due || "9").localeCompare(b.next_due || "9")).map((r) => {
                const [label, tone] = status(r);
                return (
                  <li key={r.id} className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm">
                    <button type="button" className="font-medium hover:underline" onClick={() => setOnly(only === r.vaccine.toLowerCase() ? "" : r.vaccine.toLowerCase())}>{r.vaccine}</button>
                    <span className="text-stone-500">last {dayText(r.given_on)}{r.next_due ? ` · next ${dayText(r.next_due)}` : ""}</span>
                    <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>
                  </li>
                );
              })}
            </ul>
            <p className="text-[11px] text-stone-500 mt-1">Tap a vaccine to see only its doses. We remind you a week before a dose is due.</p>
          </div>
        )}
        {earlier && <EarlierHistory h={earlier} name={pet.name} />}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-teal">History</div>
            {only && <button type="button" className="text-xs text-stone-500 underline" onClick={() => setOnly("")}>Show everything</button>}
          </div>
          {!events.length ? <p className="text-sm text-stone-500">Nothing yet. Vaccinations from NGO drives and adoptions, and visits booked on PawBazaar Care, appear here.</p> : (
            <div className="space-y-2" data-testid="pet-history-years">
              {years.map((y) => (
                <details key={y} open={y === thisYear || y === years[0]} className="rounded-xl border border-stone-200">
                  <summary className="cursor-pointer px-3 py-2 font-semibold text-sm">{y} <span className="font-normal text-stone-500">· {events.filter((e) => e.date.startsWith(y)).length}</span></summary>
                  <ul className="px-3 pb-2 space-y-1.5">
                    {events.filter((e) => e.date.startsWith(y)).map((e) => (
                      <li key={`${e.kind}-${e.id}`} className="text-sm flex gap-2">
                        <span className="w-20 shrink-0 text-stone-500 tabular-nums">{dayText(e.date).replace(/ \d{4}$/, "")}</span>
                        <span className="min-w-0"><span className="font-medium inline-flex items-center gap-1">{e.kind === "vaccine" ? <Syringe className="h-3.5 w-3.5 text-teal" /> : e.kind === "vet" ? <Stethoscope className="h-3.5 w-3.5 text-teal" /> : <Scissors className="h-3.5 w-3.5 text-marigold" />}{e.title}</span><span className="block text-xs text-stone-500">{e.sub}</span>
                          {e.visit && <VisitDetails v={e.visit} />}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          )}
        </div>
        {hist && hist.page < hist.pages && !only && (
          <Button type="button" variant="outline" size="sm" className="rounded-full self-start" onClick={() => loadHist(hist.page + 1)} data-testid="pet-history-more">Show older visits</Button>
        )}
        <DialogFooter className="gap-2">
          <a href={`${CARE_URL}/services`} className="text-sm text-teal font-semibold self-center mr-auto">Book care for {pet.name}</a>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** An adopted pet's health history from its earlier family: what they
 *  noted and the vet visits, without their name, the clinic or bills. */
function EarlierHistory({ h, name }) {
  const n = h.notes || {};
  const lines = [n.allergies && `Allergies: ${n.allergies}`, n.health_notes && `Health: ${n.health_notes}`, n.past_reactions && `Past reactions: ${n.past_reactions}`,
                 n.behaviour_notes && `Handling: ${n.behaviour_notes}`, n.triggers?.length && `Upset by: ${n.triggers.join(", ")}`].filter(Boolean);
  return (
    <details className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid="pet-earlier-history">
      <summary className="cursor-pointer font-semibold text-sky-900">Before {name} joined you ({(h.visits || []).length} vet visit{(h.visits || []).length === 1 ? "" : "s"})</summary>
      <div className="mt-2 space-y-1.5 text-stone-700">
        {lines.map((l) => <div key={l}>{l}</div>)}
        {(h.visits || []).map((v, i) => (
          <div key={i} className="border-t border-sky-100 pt-1.5">
            <span className="font-medium">{dayText(v.date)}</span> · {v.name || v.category_label}
            <VisitDetails v={{ ...v, booking_id: `earlier-${i}`, has_prescription: false }} />
          </div>
        ))}
        <p className="text-xs text-stone-500">Shared by {name}'s earlier family when they re-homed {name}. Not a vet certificate.</p>
      </div>
    </details>
  );
}

export default function MyPets({ embedded = false }) {
  const [pets, setPets] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, pet = edit
  const [removing, setRemoving] = useState(null);
  const [records, setRecords] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [rehoming, setRehoming] = useState([]);   // CUS-11 requests
  const [rehomePet, setRehomePet] = useState(null);
  // Care notes for groomers and vets (?care=<pet id> opens them, e.g. from a booking).
  const [carePet, setCarePet] = useState(null);
  const [careFor] = useState(() => new URLSearchParams(window.location.search).get("care"));
  useEffect(() => { if (careFor && pets) setCarePet(pets.find((p) => p.id === careFor) || null); }, [careFor, pets]);

  const load = useCallback(async () => {
    api.get("/community/me/rehoming").then(({ data }) => setRehoming(data || [])).catch(() => setRehoming([]));
    // Health records first: records an NGO made before you joined are matched
    // to your verified mobile/email here, and their pet is added to your list.
    try { const { data } = await api.get("/community/me/pet-records"); setRecords(data || []); } catch { setRecords([]); }
    try {
      const { data } = await api.get("/customer/pets");
      setPets(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPets((p) => p || []);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(p) {
    setRemoving(p.id);
    try {
      await api.delete(`/customer/pets/${p.id}`);
      toast.success(`${p.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className={embedded ? "max-w-3xl" : "max-w-3xl mx-auto px-4 sm:px-6 py-8"} data-testid="my-pets-page">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          {!embedded && <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">My pets</h1>}
          <p className="text-sm text-stone-600 mt-2">We use this to pick the right food, treats and care for them.</p>
        </div>
        {pets?.length > 0 && (
          <Button onClick={() => setEditing({})} data-testid="pet-add" className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Add a pet
          </Button>
        )}
      </div>

      {pets === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <Card key={i} className="h-24 rounded-2xl animate-pulse bg-stone-100" />)}</div>
      ) : pets.length === 0 ? (
        <Card className="p-10 text-center rounded-3xl border-dashed">
          <div className="h-14 w-14 rounded-2xl bg-marigold/15 text-marigold flex items-center justify-center mx-auto mb-4"><PawPrint className="h-6 w-6" /></div>
          <div className="font-heading text-xl font-semibold">Add your first pet</div>
          <p className="text-sm text-stone-600 mt-2 max-w-sm mx-auto">Just a name and whether it's a dog or a cat — everything else is optional. We'll show products picked for them.</p>
          <Button onClick={() => setEditing({})} data-testid="pet-add" className="mt-5 rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
            <Plus className="h-4 w-4 mr-1" /> Add a pet
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {pets.map((p) => (
            <Card key={p.id} className="p-4 rounded-2xl" data-testid={`pet-${p.id}`}>
             <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2">
              <PetAvatar pet={p} size={64} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-semibold truncate">{p.name}</span>
                  <Badge className={`rounded-full border-0 ${p.species === "cat" ? "bg-teal/10 text-teal" : "bg-marigold/15 text-stoneWarm"}`}>{SPECIES_LABEL[p.species]}</Badge>
                </div>
                <div className="text-sm text-stone-600">
                  {[p.breed, p.gender && p.gender !== "unknown" ? (p.gender === "male" ? "Male" : "Female") : null, ageText(p.age_months), p.weight_kg ? `${p.weight_kg} kg` : null].filter(Boolean).join(" · ") || "Add their age and breed for better picks"}
                </div>
                {p.notes && <div className="text-xs text-stone-500 truncate mt-0.5">{p.notes}</div>}
                <button type="button" onClick={() => setCarePet(p)} data-testid={`pet-care-${p.id}`}
                  className="text-xs mt-0.5 inline-flex items-center gap-1 text-teal hover:underline text-left">
                  <Scissors className="h-3.5 w-3.5" />{careSummary(p.care) || "Add care notes for groomers and vets"}</button>
                <VaccineBadge rows={records.filter((r) => r.pet_id === p.id)} />
                {p.adopted_from && (
                  <div className="text-xs text-violet-700 mt-1" data-testid={`pet-adopted-${p.id}`}>
                    Adopted from {p.adopted_from.ngo_name} on {new Date(p.adopted_from.adopted_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
              </div>
              {/* Phones: the buttons on their own row, so the name has room. */}
              <div className="w-full sm:w-auto flex items-center justify-end gap-1 shrink-0">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setViewing(p)} data-testid={`pet-view-${p.id}`}>View</Button>
              <Button variant="ghost" size="icon" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`} data-testid={`pet-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" disabled={removing === p.id} aria-label={`Remove ${p.name}`} data-testid={`pet-delete-${p.id}`}
                      onClick={() => { if (window.confirm(`Remove ${p.name}? Their profile will be deleted.`)) remove(p); }}>
                {removing === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-terracotta" />}
              </Button>
              </div>
             </div>
             {(() => {
               const req = rehoming.find((r) => r.pet_id === p.id && ["open", "matched", "expired"].includes(r.status));
               return req ? <RehomeStatus req={req} petName={p.name} onChanged={load} /> : (
                 <button type="button" onClick={() => setRehomePet(p)} data-testid={`pet-rehome-${p.id}`}
                   className="mt-2 text-xs text-stone-500 hover:text-teal inline-flex items-center gap-1"><HeartHandshake className="h-3.5 w-3.5" /> Can't keep {p.name}? Find a new home</button>
               );
             })()}
             <HealthRecords rows={records.filter((r) => r.pet_id === p.id)} />
            </Card>
          ))}
          <p className="text-sm text-stone-500 pt-2">See products picked for them on the <Link to="/" className="text-teal font-semibold hover:underline">home page</Link>.</p>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {rehoming.filter((r) => r.status === "handed_over").map((r) => (
          <div key={r.id} className="text-sm text-stone-600 rounded-xl bg-stone-50 border border-stone-200 px-4 py-2" data-testid={`pet-rehomed-${r.id}`}>
            <HeartHandshake className="h-4 w-4 inline mr-1 text-teal" /> {r.pet.name} went to {(r.offers.find((o) => o.status === "chosen") || {}).ngo_name
              || (() => { const b = (r.business_offers || []).find((o) => o.id === r.chosen_offer_id); return b ? `${b.adopter_first_name}, through ${b.biz_name},` : "a new home"; })()} on {new Date(r.handed_over_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} to find a new home.
          </div>
        ))}
      </div>
      {rehomePet && <RehomeDialog pet={rehomePet} vaccines={records.filter((r) => r.pet_id === rehomePet.id && r.kind === "vaccination").length}
        onClose={() => setRehomePet(null)} onDone={() => { setRehomePet(null); load(); }} />}
      {viewing && <PetHistory pet={viewing} records={records} onClose={() => setViewing(null)} />}
      <Dialog open={!!carePet} onOpenChange={(o) => { if (!o) setCarePet(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{carePet?.name}: care notes</DialogTitle></DialogHeader>
          {carePet && <PetCareForm key={carePet.id} pet={carePet} onClose={() => setCarePet(null)} onSaved={() => { setCarePet(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? editing.name : "Add a pet"}</DialogTitle></DialogHeader>
          {editing && (
            <PetForm key={editing.id || "new"} pet={editing.id ? editing : null} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** E-C4: the breed box. A dropdown of the breeds for the chosen kind of pet,
 *  narrowed as you type; anything typed is kept (mixed breeds, rare ones). */
const breedCache = {};
function BreedPicker({ species, value, onChange, placeholder }) {
  const [list, setList] = useState(breedCache[species] || []);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  useEffect(() => {
    if (breedCache[species]) { setList(breedCache[species]); return undefined; }
    let on = true;
    api.get("/pets/breeds", { params: { species } }).then(({ data }) => { breedCache[species] = data || []; if (on) setList(breedCache[species]); }).catch(() => {});
    return () => { on = false; };
  }, [species]);
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);
  const q = (value || "").trim().toLowerCase();
  const shown = (q ? list.filter((b) => b.toLowerCase().includes(q)) : list).slice(0, 50);
  const pick = (b) => { onChange(b); setOpen(false); };
  const keys = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && open && shown[hi]) { e.preventDefault(); pick(shown[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div className="relative" ref={box}>
      <Input id="pet-breed" data-testid="pet-breed" value={value} maxLength={60} placeholder={placeholder} autoComplete="off"
        role="combobox" aria-expanded={open} aria-controls="pet-breed-list"
        onFocus={() => setOpen(true)} onKeyDown={keys}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }} className="pr-9" />
      <button type="button" tabIndex={-1} aria-label="Show breeds" onClick={() => setOpen((o) => !o)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && shown.length > 0 && (
        <ul id="pet-breed-list" role="listbox" data-testid="breed-list"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-stone-200 bg-white py-1 shadow-lg">
          {shown.map((b, i) => (
            <li key={b} role="option" aria-selected={b === value} data-testid="breed-option"
              onMouseDown={(e) => { e.preventDefault(); pick(b); }} onMouseEnter={() => setHi(i)}
              className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${i === hi ? "bg-marigold/10" : ""} ${b === value ? "font-semibold text-stoneWarm" : "text-stone-700"}`}>
              {b}{b === value && <Check className="h-4 w-4 text-teal" />}
            </li>))}
        </ul>
      )}
    </div>
  );
}
